import type { PaginationState, Row, RowSelectionState, SortingState } from '@tanstack/react-table';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';
import { cn, NoddiIcon } from 'noddi-ui-common';
import { useMemo, useState } from 'react';
import { useIsMobile } from '../../../hooks/useIsMobile';
import type { TableQuickFilter } from '../../../types/table';
import { NoddiResponsiveCard } from '../../Cards/NoddiResponsiveCard';
import { NoddiInput } from '../../Inputs/NoddiInput';
import { DEFAULT_PAGE_SIZE } from '../../Navigation/Pagination/Pagination/paginationDefaults';
import { BaseTable } from './components/BaseTable';
import { FilterUnitControls } from './components/FilterSidebar/FilterUnitControls';
import { QuickFilterTray } from './components/QuickFilterTray';
import { RowSelectionAction, RowSelectionActionBar } from './components/RowSelectionActionBar';
import { TablePagination } from './components/TablePagination';
import { ViewToggle } from './components/ViewToggle';
import type { FilterUnit } from './filterUnitType';
import { normalizeSearchText } from './helper';
import { getColumnSortKey } from './sortOrdering';
import type { NoddiTableTranslations } from './tableTranslationTypes';
import { resolveNoddiTableTranslations } from './tableTranslationTypes';
import type { BaseTableCommonProps } from './types';
import { useGetProcessedColumns } from './useGetProcessedColumns';

interface StaticTableProps<
  TData,
  TValue,
  TOrderingKey extends string = string,
  TQuickFilterValue extends string = string
> extends BaseTableCommonProps<TData, TValue, TOrderingKey> {
  hideColumnToggle?: boolean;
  /** Detail sections can show every row without a nested vertical scroll area. */
  heightMode?: 'viewport' | 'content';
  translations?: NoddiTableTranslations;
  getSearchText?: (row: TData) => string;
  /**
   * Hide the "Total of N item(s)" line. Set it when `data` is a truncated page
   * (a card showing the first few rows of many) — the count is of the rows
   * handed in, so it would otherwise state the page size as the total.
   */
  hideRowCount?: boolean;
  enablePagination?: boolean;
  enableSearch?: boolean;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  searchPlaceholder?: string;
  /** Single-select scope beside View, as on `NoddiTable`. The consumer filters `data`. */
  quickFilter?: TableQuickFilter<TQuickFilterValue>;
  /**
   * `filterUnitFor` builders resolved by `useFilterUnits`, as on `NoddiTable`: the Filters button and sheet, plus the
   * unit's date range and quick filter. The consumer spreads `filters.apiInput` into the query that loads `data`.
   */
  filters?: FilterUnit<object>;
  rowSelection?: RowSelectionState;
  setRowSelection?: (rowSelection: RowSelectionState | ((prev: RowSelectionState) => RowSelectionState)) => void;
  enableRowSelection?: boolean | ((row: Row<TData>) => boolean);
  rowSelectionActions?: RowSelectionAction[];
  /** Contextual line shown under the count in the bottom selection bar. Compute from the selected rows in the consumer. */
  rowSelectionSubtitle?: string;
  /**
   * How many rows match the current filter in total, which for a static table is
   * not necessarily how many are rendered -- a payload can be capped for size
   * while the consumer still knows the full count. Enables "Select all N matching".
   */
  rowSelectionTotalMatchingCount?: number;
  /** When set with row selection, shows "Select all N matching" while fewer than N are selected. Mirrors NoddiTable. */
  rowSelectionSelectAllMatching?: {
    onSelectAllMatching: () => void;
    isLoading: boolean;
    /** When set, used instead of `translations.rowSelectionBottomBar.selectAllMatching`. */
    selectAllMatchingLabel?: (totalMatchingCount: number) => string;
  };
}

const EMPTY_SORTING: SortingState = [];
const EMPTY_ROWS: never[] = [];

// table with support for client side pagination and search
export function NoddiStaticTable<
  TData,
  TValue,
  TOrderingKey extends string = string,
  TQuickFilterValue extends string = string
>({
  columns = [],
  data = EMPTY_ROWS,
  isPending,
  getRowId,
  headerNode,
  title,
  titleCount,
  description,
  icon,
  iconTile,
  getUrlPath,
  wrapInCard = false,
  skeletonRowCount = 10,
  containerClassName,
  columnSize,
  hideColumnToggle,
  heightMode = 'viewport',
  translations,
  getSearchText,
  hideRowCount = false,
  customNode,
  enablePagination = false,
  enableSearch = true,
  pageSizeOptions = [10, 15, 30, 40, 50, 100],
  initialPageSize = DEFAULT_PAGE_SIZE,
  searchPlaceholder = 'Search...',
  quickFilter,
  filters,
  rowSelection,
  setRowSelection,
  enableRowSelection,
  rowSelectionActions,
  rowSelectionSubtitle,
  rowSelectionTotalMatchingCount,
  rowSelectionSelectAllMatching,
  onRowClick,
  onRowContextMenu,
  actions,
  disableRowMenu,
  hideCopyRowId,
  emptyStateText,
  ordering,
  onOrderChange
}: StaticTableProps<TData, TValue, TOrderingKey, TQuickFilterValue>) {
  const resolvedTranslations = resolveNoddiTableTranslations(translations);
  const isMobile = useIsMobile();

  // Pagination state
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: enablePagination ? initialPageSize : data.length || 9999
  });

  // Search state
  const [globalFilter, setGlobalFilter] = useState('');
  // A unit's tray writes its own param; a plain `quickFilter` is the consumer's. A table has one tray.
  const tray: TableQuickFilter<string> | undefined = filters?.quickFilter ?? quickFilter;
  const resetPageIndex = () => setPagination((previous) => ({ ...previous, pageIndex: 0 }));

  const searchedData = useMemo(() => {
    if (!getSearchText) {
      return data;
    }
    const needle = normalizeSearchText(globalFilter);
    if (!needle) {
      return data;
    }
    return data.filter((row) => normalizeSearchText(getSearchText(row)).includes(needle));
  }, [data, getSearchText, globalFilter]);

  const { processedColumns } = useGetProcessedColumns({
    columns,
    enableRowSelection: !!setRowSelection
  });

  const defaultColumnSize = useMemo(() => {
    return columnSize ? columnSize : isMobile ? 100 : 150;
  }, [columnSize, isMobile]);

  // Keeps its identity between renders: TanStack re-sorts every row whenever `state.sorting` is a
  // new array.
  const sorting = useMemo<SortingState>(() => {
    if (!ordering || ordering.length === 0) {
      return EMPTY_SORTING;
    }
    return ordering.flatMap((sort) => {
      const column = processedColumns.find((candidate) => getColumnSortKey({ meta: candidate.meta }) === sort.column);
      const columnId =
        column?.id ?? (column && 'accessorKey' in column ? String(column.accessorKey).replaceAll('.', '_') : undefined);
      return columnId && sort.order ? [{ id: String(columnId), desc: sort.order === 'desc' }] : [];
    });
  }, [ordering, processedColumns]);

  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack useReactTable returns functions the React Compiler cannot memoize; compiler skipping this component is accepted
  const table = useReactTable({
    data: searchedData,
    columns: processedColumns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: enablePagination ? getPaginationRowModel() : undefined,
    onRowSelectionChange: setRowSelection,
    getRowId: (row) => String(getRowId(row)),
    defaultColumn: {
      size: defaultColumnSize
    },
    // TanStack queues a page-index reset on every row-model recompute (any new `data` or `sorting`
    // array) and always writes a new object; keeping the unchanged one stops it re-rendering.
    onPaginationChange: (updater) =>
      setPagination((previous) => {
        const next = typeof updater === 'function' ? updater(previous) : updater;
        return next.pageIndex === previous.pageIndex && next.pageSize === previous.pageSize ? previous : next;
      }),
    state: {
      pagination,
      sorting,
      // `getSearchText` searches the whole row (including values no column renders), which
      // the table's own filter cannot express -- it runs per column and ORs the results, so
      // it would re-normalize the same string once per column. `searchedData` is already
      // filtered above, so the table's filter is held off rather than run twice.
      globalFilter: getSearchText ? '' : globalFilter,
      ...(rowSelection !== undefined && { rowSelection })
    },
    // Global search
    onGlobalFilterChange: setGlobalFilter,
    // Match compact registrations and formatted phones regardless of separators in either spelling.
    globalFilterFn: (row, columnId, query: string) =>
      normalizeSearchText(String(row.getValue(columnId) ?? '')).includes(normalizeSearchText(query)),
    enableRowSelection: enableRowSelection ?? !!setRowSelection
  });

  const handlePaginationChange = (newPagination: { pageIndex: number; pageSize: number }) => {
    setPagination(newPagination);
  };

  return (
    <NoddiResponsiveCard
      title={title}
      titleCount={titleCount}
      description={description}
      icon={icon}
      iconTile={iconTile}
      headerNode={headerNode}
      wrapInCard={wrapInCard}
      className={cn(
        // Match NoddiTable height constraints for proper scrolling behavior
        'flex flex-col',
        heightMode === 'viewport' && 'max-h-[calc(100vh-8rem)] md:max-h-[calc(100vh-18rem)]'
      )}
      contentClassName='flex min-h-0 flex-1'
    >
      <div className='flex min-h-0 w-full flex-1 flex-col gap-2'>
        <div className='flex flex-wrap-reverse items-center justify-between gap-2'>
          {/* Search Input */}
          <div className='flex flex-wrap-reverse items-center gap-2'>
            {filters && (
              <FilterUnitControls
                filters={filters}
                resetPagination={resetPageIndex}
                translations={resolvedTranslations.filter}
              />
            )}
            {enableSearch && (
              <div className='relative'>
                <NoddiInput
                  placeholder={searchPlaceholder}
                  className='w-50 lg:min-w-70'
                  value={globalFilter}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  startAdornment={
                    <NoddiIcon className={cn(!globalFilter && 'text-muted-foreground')} name='Search' size='small' />
                  }
                />
              </div>
            )}
            {customNode}
          </div>
          {/* min-w-0: a quick-filter tray wider than a phone scrolls inside this row, not the page. */}
          <div className='flex max-w-full min-w-0 flex-wrap items-center justify-end gap-2'>
            {tray && (
              <QuickFilterTray
                quickFilter={{
                  ...tray,
                  onChange: (value) => {
                    resetPageIndex();
                    tray.onChange(value);
                  }
                }}
              />
            )}
            {!hideColumnToggle && <ViewToggle table={table} translations={{ view: resolvedTranslations.view }} />}
          </div>
        </div>

        <div className='flex min-h-0 flex-1 flex-col overflow-hidden'>
          <BaseTable
            table={table}
            isPending={isPending}
            skeletonRowCount={skeletonRowCount}
            containerClassName={containerClassName}
            getUrlPath={getUrlPath}
            onRowClick={onRowClick}
            onRowContextMenu={onRowContextMenu}
            actions={actions}
            disableRowMenu={disableRowMenu}
            hideCopyRowId={hideCopyRowId}
            ordering={ordering}
            onOrderChange={onOrderChange}
            emptyStateText={emptyStateText ?? resolvedTranslations.emptyState.noResults}
            translations={{
              contextMenu: resolvedTranslations.contextMenu,
              columnHeader: resolvedTranslations.columnHeader,
              pagination: resolvedTranslations.pagination,
              row: resolvedTranslations.row
            }}
          />
        </div>

        {rowSelectionActions &&
          rowSelectionActions.length > 0 &&
          rowSelection &&
          setRowSelection &&
          Object.keys(rowSelection).length > 0 && (
            <RowSelectionActionBar
              selectedCount={Object.keys(rowSelection).length}
              totalMatchingCount={rowSelectionTotalMatchingCount}
              actions={rowSelectionActions}
              onClearSelection={() => setRowSelection({})}
              onSelectAllMatching={rowSelectionSelectAllMatching?.onSelectAllMatching}
              isSelectAllMatchingLoading={rowSelectionSelectAllMatching?.isLoading}
              selectAllMatchingLabel={rowSelectionSelectAllMatching?.selectAllMatchingLabel}
              subtitle={rowSelectionSubtitle}
              translations={resolvedTranslations.rowSelectionBottomBar}
            />
          )}

        {/* Pagination */}
        {enablePagination && (
          <TablePagination
            pagination={pagination}
            totalCount={table.getFilteredRowModel().rows.length}
            onChange={handlePaginationChange}
            pageSizeOptions={pageSizeOptions}
            loading={isPending}
            selectedCount={!!setRowSelection && rowSelection ? Object.keys(rowSelection).length : undefined}
            translations={resolvedTranslations.pagination}
          />
        )}

        {/* Row count when pagination is disabled */}
        {!enablePagination && !hideRowCount && !isPending && (
          <div className='px-0.5 text-muted-foreground'>
            {resolvedTranslations.pagination.totalOf} {table.getFilteredRowModel().rows.length}{' '}
            {resolvedTranslations.pagination.item}
          </div>
        )}
      </div>
    </NoddiResponsiveCard>
  );
}
